import { MANUFACTURERBOD_PLACEHOLDERS } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import type { ContentItemViewNodesType, DataNode } from '../types'

/**
 * Determines if a manufacturer/BOD node is a header (parent) or a pill (child).
 * Uses 3-step detection:
 * 1. Check if it's a manufacturer/BOD node (optimization)
 * 2. PRIMARY: Trust dtvContentItemParentId null/undefined (both mean header)
 * 3. FALLBACK: Use text matching when ID has a value
 *
 * @param {DataNode | ContentItemViewNodesType} node - The node to check
 * @returns {boolean} True if the node is a manufacturer/BOD header (parent), false otherwise
 */
export const isManufacturerOrBODHeader = (
    node: DataNode | Partial<ContentItemViewNodesType>
): boolean => {
    // STEP 1: Only process manufacturer/BOD nodes
    if (!node.isManufacturers && !node.isBOD) {
        return false
    }

    // STEP 2: PRIMARY - if ID is null or undefined → it's a header
    // Handle both DataNode (has contentItemView wrapper) and ContentItemViewNodesType (direct properties)
    const parentId =
        'contentItemView' in node
            ? node.contentItemView?.dtvContentItemParentId
            : (node as Partial<ContentItemViewNodesType>).dtvContentItemParentId

    if (parentId == null) {
        // catches both null and undefined
        return true // It's a header
    }

    // STEP 3: FALLBACK - if ID has a value → verify with text matching
    const placeHolders = Object.values(MANUFACTURERBOD_PLACEHOLDERS) as string[]

    // Get text from either DataNode or ContentItemViewNodesType structure
    const firstElementText =
        'contentItemNodes' in node
            ? (node as DataNode).contentItemNodes?.[0]?.text || ''
            : (node as Partial<ContentItemViewNodesType>).elements?.[0]?.text ||
              ''

    // Normalize text: remove colons and trim whitespace for comparison
    const normalizedText = firstElementText.replace(/:/g, '').trim()

    // Check if text starts with any placeholder (handles "Manufacturers:", "Basis-of-Design Product: Provide", etc.)
    // Using startsWith instead of includes to avoid false positives like "Jager Engineered Wood Products" matching "Products"
    const isHeaderByText = placeHolders.some((placeholder) =>
        normalizedText.toLowerCase().startsWith(placeholder.toLowerCase())
    )

    return isHeaderByText
}

/** True for synthetic button rows (product-library-btn, om-add-mfr-btn) injected by getChildrenWithProductLibraryButton - never real content. */
export const isSyntheticButtonKey = (key: string): boolean =>
    key.startsWith('product-library-btn') || key.startsWith('om-add-mfr-btn')

/** True when the first element text contains any MANUFACTURERBOD_PLACEHOLDERS value — locks MFR/BOD header rows as read-only. */
export const isManufacturerOrBODKeyedPara = (
    node: DataNode | Partial<ContentItemViewNodesType>
): boolean => {
    const firstElementText =
        'contentItemNodes' in node
            ? (node as DataNode).contentItemNodes?.[0]?.text
            : (node as Partial<ContentItemViewNodesType>).elements?.[0]?.text

    if (!firstElementText) return false

    const normalizedText = firstElementText.replace(/:/g, '').trim().toLowerCase()
    return Object.values(MANUFACTURERBOD_PLACEHOLDERS).some((placeholder) =>
        normalizedText.startsWith(placeholder.toLowerCase())
    )
}

/**
 * Checks if the node is a manufacturer or BOD content item (pill, not header)
 * @param {DataNode} node - The node to check
 * @returns {boolean} True if the node is a manufacturer or BOD pill (child), false otherwise
 */
export const isManufacturerOrBODPill = (node: DataNode): boolean => {
    // Must be a manufacturer/BOD node AND must NOT be a header
    if (!node.isManufacturers && !node.isBOD) {
        return false // Not a manufacturer/BOD node at all
    }
    return !isManufacturerOrBODHeader(node) // It is manufacturer/BOD, check if it's a pill (not header)
}
